import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LogStatistics as LogStats, LogFilter } from '../../../../types/ApplicationLog';
import logApi from '../../../../services/logApi';

interface LogStatisticsProps {
  filter: LogFilter;
  onClose: () => void;
}

const LogStatistics: React.FC<LogStatisticsProps> = ({ filter, onClose }) => {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState<LogStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, [filter.fromDate, filter.toDate]);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const stats = await logApi.getStatistics(filter.fromDate, filter.toDate);
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
      case 'critical':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'information':
        return 'bg-blue-500';
      case 'debug':
        return 'bg-gray-500';
      default:
        return 'bg-gray-400';
    }
  };

  const calculatePercentage = (count: number, total: number) => {
    if (total === 0) return 0;
    return ((count / total) * 100).toFixed(1);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('warehouse.modules.logs.statistics')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">{t('common.status.loading')}</div>
            </div>
          ) : statistics ? (
            <div className="space-y-6">
              {/* Date Range */}
              {(statistics.fromDate || statistics.toDate) && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{t('warehouse.modules.logs.period')}:</span>{' '}
                  {statistics.fromDate && new Date(statistics.fromDate).toLocaleDateString('nb-NO')}
                  {statistics.fromDate && statistics.toDate && ' - '}
                  {statistics.toDate && new Date(statistics.toDate).toLocaleDateString('nb-NO')}
                </div>
              )}

              {/* Total Count */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-3xl font-bold text-gray-900">{statistics.totalCount}</div>
                <div className="text-sm text-gray-600">{t('warehouse.modules.logs.totalLogEntries')}</div>
              </div>

              {/* Level Distribution */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t('warehouse.modules.logs.distribution')}
                </h3>
                <div className="space-y-3">
                  {Object.entries(statistics.levelCounts)
                    .filter(([level]) => level !== 'Total')
                    .sort((a, b) => b[1] - a[1])
                    .map(([level, count]) => (
                      <div key={level} className="flex items-center">
                        <div className="w-24 text-sm font-medium text-gray-700">
                          {level}
                        </div>
                        <div className="flex-1 mx-4">
                          <div className="bg-gray-200 rounded-full h-6 relative overflow-hidden">
                            <div
                              className={`h-full ${getLevelColor(level)} transition-all duration-300`}
                              style={{ width: `${calculatePercentage(count, statistics.totalCount)}%` }}
                            />
                          </div>
                        </div>
                        <div className="w-20 text-right">
                          <span className="text-sm font-medium text-gray-900">{count}</span>
                          <span className="text-xs text-gray-500 ml-1">
                            ({calculatePercentage(count, statistics.totalCount)}%)
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-600">
                    {(statistics.levelCounts['Error'] || 0) + (statistics.levelCounts['Critical'] || 0)}
                  </div>
                  <div className="text-sm text-red-700">{t('warehouse.modules.logs.errors')}</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-yellow-600">
                    {statistics.levelCounts['Warning'] || 0}
                  </div>
                  <div className="text-sm text-yellow-700">{t('warehouse.modules.logs.warnings')}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500">
              {t('warehouse.modules.logs.noStatistics')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            {t('common.buttons.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogStatistics;